import { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Loader2, ShieldCheck, ShieldAlert, Receipt } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { BRAND_COLORS, STATUS_COLORS, withAlpha } from "../lib/bookingStatus";
import { deDate } from "../lib/dateFormat";
import { Switch } from "../components/ui/switch";
import { AdminPinPromptDialog } from "../components/admin/AdminPinPromptDialog";
import { bumpFeatureFlags } from "../lib/featureFlags";

/**
 * Feb 2026 · Admin-Einstellung: 4-stelliger PIN für sensible Aktionen
 * (Kundenprofil bearbeiten). Werksstandard ist `0611`, solange der Admin
 * noch keine eigene PIN vergeben hat.
 *
 * Sicherheitsabfrage: Um die PIN zu ändern MUSS die aktuelle PIN
 * bestätigt werden — das verhindert, dass eine kurz unbeobachtete Session
 * missbraucht wird. Die PIN wird nur gehasht (bcrypt) in `db.settings`
 * gespeichert; sie kann nicht ausgelesen werden.
 */
export default function TeamAdminPin() {
  const [state, setState] = useState(null);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  // 20 Jun 2026 · Feature-Flag „objektbezogene Rechnungen" (admin-schaltbar).
  const [invoicesEnabled, setInvoicesEnabled] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [pendingEnabled, setPendingEnabled] = useState(false);
  const [flagSaving, setFlagSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/team/settings/edit-pin");
      setState(data);
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || err.message);
    }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    api.get("/config/features")
      .then(({ data }) => setInvoicesEnabled(!!data?.property_invoices_enabled))
      .catch(() => {});
  }, []);

  const requestToggle = (nextEnabled) => {
    setPendingEnabled(nextEnabled);
    setPinOpen(true);
  };

  const applyToggle = async () => {
    setPinOpen(false);
    setFlagSaving(true);
    try {
      const { data } = await api.put("/team/feature-flags/property-invoices", { enabled: pendingEnabled });
      setInvoicesEnabled(!!data?.property_invoices_enabled);
      bumpFeatureFlags();
      toast.success(pendingEnabled
        ? "Objektbezogene Rechnungen aktiviert."
        : "Objektbezogene Rechnungen deaktiviert.");
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || err.message);
    } finally {
      setFlagSaving(false);
    }
  };

  const digitInput = /^\d{0,4}$/;

  const save = async () => {
    if (!/^\d{4}$/.test(next)) {
      toast.error("Neuer PIN muss aus 4 Ziffern bestehen.");
      return;
    }
    if (next !== confirm) {
      toast.error("Bestätigung stimmt nicht überein.");
      return;
    }
    if (!/^\d{4}$/.test(current)) {
      toast.error("Bitte aktuellen PIN eingeben.");
      return;
    }
    setSaving(true);
    try {
      await api.put("/team/settings/edit-pin", {
        current_pin: current,
        new_pin: next,
      });
      toast.success("PIN gespeichert.");
      setCurrent("");
      setNext("");
      setConfirm("");
      await load();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        toast.error("Aktueller PIN ist falsch.");
      } else {
        toast.error(formatErr(err?.response?.data?.detail) || err.message);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="max-w-[720px] mx-auto px-4 md:px-10 py-8 md:py-12"
      data-testid="team-admin-pin"
    >
      <div className="mb-6">
        <div className="label-caps mb-1">Sicherheit</div>
        <h1 className="font-display text-3xl md:text-4xl text-navy leading-tight">
          Bearbeitungs-PIN
        </h1>
        <p className="mt-3 text-[13.5px] text-navy-dark/70 leading-snug max-w-[560px]">
          Der 4-stellige PIN wird vor sensiblen Bearbeitungen abgefragt (z. B.
          Kundenprofil ändern). So verhindern wir versehentliche Änderungen
          bei kurz unbeobachteten Sessions. Der Werksstandard ist{" "}
          <span className="font-mono">0611</span> — bitte einmalig auf einen
          eigenen Wert setzen.
        </p>
      </div>

      {state?.is_default && (
        <div
          className="mb-6 p-3 border-l-4 flex items-start gap-3"
          style={{
            borderLeftColor: STATUS_COLORS.red,
            background: withAlpha(STATUS_COLORS.red, 0.05),
          }}
          data-testid="team-admin-pin-default-warning"
        >
          <ShieldAlert
            className="w-4 h-4 mt-0.5 shrink-0"
            strokeWidth={1.8}
            style={{ color: STATUS_COLORS.red }}
          />
          <div className="text-[12.5px] text-navy-dark/80">
            <strong>Aktuell ist der Werksstandard `0611` aktiv.</strong>
            <br />
            Bitte ändern Sie den PIN auf einen eigenen Wert.
          </div>
        </div>
      )}

      {state && !state.is_default && (
        <div
          className="mb-6 p-3 border-l-4 flex items-start gap-3"
          style={{
            borderLeftColor: STATUS_COLORS.green,
            background: withAlpha(STATUS_COLORS.green, 0.05),
          }}
          data-testid="team-admin-pin-custom-note"
        >
          <ShieldCheck
            className="w-4 h-4 mt-0.5 shrink-0"
            strokeWidth={1.8}
            style={{ color: STATUS_COLORS.green }}
          />
          <div className="text-[12.5px] text-navy-dark/80">
            Eigener PIN aktiv{state.updated_at && ` (zuletzt geändert am ${deDate(state.updated_at)})`}.
          </div>
        </div>
      )}

      <div className="bg-white border border-navy/10 p-5 md:p-6 space-y-4">
        <Field label="Aktueller PIN">
          <input
            type="password"
            inputMode="numeric"
            maxLength={4}
            value={current}
            onChange={(e) => digitInput.test(e.target.value) && setCurrent(e.target.value)}
            data-testid="team-admin-pin-current"
            className="w-32 bg-white border border-navy/25 focus:border-gold focus:outline-none px-3 py-2 text-center text-lg font-mono tracking-widest"
            autoComplete="current-password"
            placeholder="••••"
          />
        </Field>
        <div className="border-t border-navy/5 pt-4">
          <Field label="Neuer PIN">
            <input
              type="password"
              inputMode="numeric"
              maxLength={4}
              value={next}
              onChange={(e) => digitInput.test(e.target.value) && setNext(e.target.value)}
              data-testid="team-admin-pin-new"
              className="w-32 bg-white border border-navy/25 focus:border-gold focus:outline-none px-3 py-2 text-center text-lg font-mono tracking-widest"
              autoComplete="new-password"
              placeholder="••••"
            />
          </Field>
        </div>
        <Field label="Neuer PIN wiederholen">
          <input
            type="password"
            inputMode="numeric"
            maxLength={4}
            value={confirm}
            onChange={(e) => digitInput.test(e.target.value) && setConfirm(e.target.value)}
            data-testid="team-admin-pin-confirm"
            className="w-32 bg-white border border-navy/25 focus:border-gold focus:outline-none px-3 py-2 text-center text-lg font-mono tracking-widest"
            autoComplete="new-password"
            placeholder="••••"
          />
        </Field>

        <div className="pt-2 border-t border-navy/5 flex justify-end">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="team-admin-pin-save"
            className="inline-flex items-center gap-1.5 bg-navy text-ivory hover:bg-navy-dark disabled:opacity-60 px-4 py-2 text-[12px] tracking-wider uppercase"
            style={{ background: BRAND_COLORS.navy }}
          >
            {saving ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <KeyRound className="w-3.5 h-3.5" strokeWidth={1.8} />
            )}
            PIN speichern
          </button>
        </div>
      </div>

      <div
        className="mt-8 bg-white border border-navy/10 p-5 md:p-6"
        data-testid="feature-invoices-card"
      >
        <div className="flex items-start gap-3 mb-3">
          <Receipt
            className="w-4 h-4 mt-0.5 shrink-0"
            strokeWidth={1.8}
            style={{ color: BRAND_COLORS.gold }}
          />
          <div>
            <h2 className="font-display text-xl text-navy leading-tight">
              Objektbezogene Rechnungen
            </h2>
            <p className="mt-1 text-[13px] text-navy-dark/70 leading-snug max-w-[520px]">
              Standardmäßig aus — die aktive Rechnungsstellung läuft über den
              sevDesk-Upload. Beim Einschalten erscheinen die Menüpunkte
              „Rechnungen", „Rechnungsvorlage" und „Rechnungsarchiv" wieder und
              Rechnungen können erneut direkt in der App erstellt werden.
            </p>
          </div>
        </div>

        <div
          className="p-3 border-l-4 mb-4 text-[12.5px] text-navy-dark/80"
          style={{
            borderLeftColor: STATUS_COLORS.red,
            background: withAlpha(STATUS_COLORS.red, 0.05),
          }}
        >
          <strong>Achtung:</strong> Bei aktiver App-Rechnungsstellung laufen zwei
          Nummernkreise parallel (App-Rechnungen und sevDesk). Bitte nur
          einschalten, wenn objektbezogene Rechnungen wirklich wieder benötigt
          werden.
        </div>

        <div className="flex items-center justify-between border-t border-navy/5 pt-4">
          <span className="text-[13px] text-navy-dark/80">
            {invoicesEnabled ? "Aktiviert" : "Deaktiviert"}
          </span>
          <Switch
            checked={invoicesEnabled}
            disabled={flagSaving}
            onCheckedChange={(v) => requestToggle(!!v)}
            data-testid="feature-invoices-toggle"
          />
        </div>
      </div>

      {pinOpen && (
        <AdminPinPromptDialog
          title="Rechnungs-Schalter bestätigen"
          description={
            pendingEnabled
              ? "Bitte mit dem Admin-PIN bestätigen, um objektbezogene Rechnungen zu aktivieren."
              : "Bitte mit dem Admin-PIN bestätigen, um objektbezogene Rechnungen zu deaktivieren."
          }
          purpose="property-invoices-toggle"
          onClose={() => setPinOpen(false)}
          onSuccess={applyToggle}
        />
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="flex items-center justify-between gap-4">
      <span className="text-[13px] text-navy-dark/75">{label}</span>
      {children}
    </label>
  );
}
